/**
 * Interpolation for everyone who is not you.
 *
 * Remote actors arrive as a position ten times a second. Drawn as they arrive,
 * they hop a whole tile at a time; the design calls for them to glide (M0-05:
 * "remote players glide, never teleport"). So the client draws them slightly
 * in the past — far enough back that the next update has always landed — and
 * slides between the last two positions it was told about.
 *
 * The cost is that everyone else is one buffer-length behind where the server
 * says they are. That is a real trade, and the tick model is what makes it
 * affordable: at two ticks per tile, a hundred milliseconds is a fifth of a
 * step, and deliberate combat (M2-01) resolves on tick boundaries the server
 * decides rather than on what the client drew.
 */

import { MS_PER_TICK } from '@annwn/protocol';

/**
 * How far behind live the client draws remote actors.
 *
 * One snapshot interval — enough that a packet arriving slightly late finds
 * the animation still running rather than already finished and holding. It
 * cannot usefully be much larger: a step is drawn over its full duration, so
 * the delay is pure latency on top of that, and other players start to feel
 * detached from what the server thinks they are doing.
 */
export const INTERPOLATION_DELAY_MS = MS_PER_TICK;

export interface Interpolated {
  /** Tile coordinates, fractional — the point of the exercise. */
  readonly x: number;
  readonly y: number;
}

export interface InterpolationSource {
  readonly x: number;
  readonly y: number;
  readonly at: number;
  readonly from: { x: number; y: number } | null;
  /** How long this step should take. See `RemoteEntity.duration`. */
  readonly duration: number;
}

/**
 * Where to draw an actor at time `now`.
 *
 * Actors that have not moved sit exactly on their tile — no easing, no
 * overshoot. Actors mid-step are somewhere between the two tiles, and an actor
 * whose update is overdue holds at its destination rather than sliding past it,
 * because extrapolating a walk that may have stopped puts people through walls.
 *
 * The step is drawn over the duration the *server* takes to walk it, so
 * consecutive steps join end to end and continuous walking has no seam in it.
 */
export function interpolate(
  entity: InterpolationSource,
  now: number,
  delayMs = INTERPOLATION_DELAY_MS,
): Interpolated {
  if (!entity.from) return { x: entity.x, y: entity.y };

  // A zero duration is a snap, not a slide — a spawn or a correction.
  if (entity.duration <= 0) return { x: entity.x, y: entity.y };

  const elapsed = now - delayMs - entity.at;

  // Still inside the delay: the move has not visually started yet.
  if (elapsed <= 0) return { x: entity.from.x, y: entity.from.y };

  const t = Math.min(1, elapsed / entity.duration);
  return {
    x: entity.from.x + (entity.x - entity.from.x) * t,
    y: entity.from.y + (entity.y - entity.from.y) * t,
  };
}

/**
 * A camera that eases toward a target instead of snapping to it.
 *
 * Framerate-independent: the smoothing is expressed as the fraction of the
 * remaining distance covered per 16 ms, so the camera behaves the same at 30
 * and 144 fps rather than becoming twitchy on fast displays.
 */
export class Camera {
  x: number;
  y: number;

  constructor(
    x = 0,
    y = 0,
    /** Fraction of the gap closed per 16 ms. 1 snaps instantly. */
    private readonly smoothing = 0.25,
  ) {
    this.x = x;
    this.y = y;
  }

  snapTo(x: number, y: number): void {
    this.x = x;
    this.y = y;
  }

  follow(x: number, y: number, deltaMs: number): void {
    if (this.smoothing >= 1) return this.snapTo(x, y);

    const steps = Math.max(0, deltaMs) / 16;
    const t = 1 - Math.pow(1 - this.smoothing, steps);

    this.x += (x - this.x) * t;
    this.y += (y - this.y) * t;

    // Settle exactly, so a camera drifting by a millionth of a tile does not
    // keep the renderer marking itself dirty forever.
    if (Math.abs(x - this.x) < 0.001) this.x = x;
    if (Math.abs(y - this.y) < 0.001) this.y = y;
  }
}
